import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { ClipboardEvent } from 'react';
import {
    CheckCircleIcon,
    WarningIcon,
    ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { TextField } from '@/components/TextField';
import { parseStreamUrl } from '@/utils/connect-camera';
import { AnalysisSettings } from '@/features/NewSourceModal/components/AnalysisSettings';
import { Spinner } from '@/components/Spinner';
import { formatRelativeTimeI18n } from '@/utils/date';
import {
    CameraCreationItem,
    CameraConnectionStatus,
    ConnectionStatus,
} from '@/types/camera';

interface AddCameraModalConfigurationProps {
    camera: CameraCreationItem;
    onCameraUpdate: (
        cameraId: string,
        updates: Partial<CameraCreationItem>,
    ) => void;
    onValidationChange: (cameraId: string, valid: boolean) => void;
    connectionStatus?: CameraConnectionStatus;
    onRetryConnection: () => void;
    triggerValidation?: number;
}

type CameraFormData = Omit<CameraCreationItem, 'id' | 'status'>;

export function AddCameraModalConfiguration({
    camera,
    onCameraUpdate,
    onValidationChange,
    connectionStatus,
    onRetryConnection,
    triggerValidation,
}: AddCameraModalConfigurationProps) {
    const hasShownStatusRef = useRef(false);

    const {
        register,
        formState: { errors, isValid },
        watch,
        setValue,
        reset,
        trigger,
    } = useForm<CameraFormData>({
        mode: 'onChange',
        defaultValues: {
            name: camera.name,
            streamUrl: camera.streamUrl,
            username: camera.username,
            password: camera.password,
            faceRecognition: camera.faceRecognition,
            licensePlateRecognition: camera.licensePlateRecognition,
            opticalCharacterRecognition: camera.opticalCharacterRecognition,
            analysisFramesPerSecond: camera.analysisFramesPerSecond,
        },
    });

    const applyStreamUrlInput = (raw: string) => {
        const { streamUrl, username, password } = parseStreamUrl(raw);
        setValue('streamUrl', streamUrl, { shouldValidate: true });
        if (username !== undefined) {
            setValue('username', username, { shouldValidate: true });
        }
        if (password !== undefined) {
            setValue('password', password, { shouldValidate: true });
        }
    };

    const handleStreamUrlPaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const pastedText = e.clipboardData.getData('text');

        const hasProtocol = /^(rtsp|http|https):\/\//i.test(pastedText);
        const hasCredentials =
            parseStreamUrl(pastedText).username !== undefined;

        if (hasProtocol || hasCredentials) {
            e.preventDefault();
            applyStreamUrlInput(pastedText);
        }
    };

    const handleStreamUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        applyStreamUrlInput(e.target.value);
    };

    useEffect(() => {
        reset({
            name: camera.name,
            streamUrl: camera.streamUrl,
            username: camera.username,
            password: camera.password,
            faceRecognition: camera.faceRecognition,
            licensePlateRecognition: camera.licensePlateRecognition,
            opticalCharacterRecognition: camera.opticalCharacterRecognition,
            analysisFramesPerSecond: camera.analysisFramesPerSecond,
        });
    }, [camera.id, reset]);

    useEffect(() => {
        const subscription = watch((value) => {
            onCameraUpdate(camera.id, value as Partial<CameraCreationItem>);
        });
        return () => subscription.unsubscribe();
    }, [watch, camera.id, onCameraUpdate]);

    useEffect(() => {
        onValidationChange(camera.id, isValid);
    }, [isValid, camera.id, onValidationChange]);

    useEffect(() => {
        if (triggerValidation && triggerValidation > 0) {
            trigger();
        }
    }, [triggerValidation, trigger]);

    const shouldAnimate =
        !hasShownStatusRef.current &&
        (connectionStatus?.status === ConnectionStatus.SUCCESS ||
            connectionStatus?.status === ConnectionStatus.ERROR ||
            connectionStatus?.status === ConnectionStatus.TESTING);

    if (shouldAnimate) {
        hasShownStatusRef.current = true;
    }

    return (
        <form className="flex flex-col gap-4">
            <div>
                <TextField
                    id={`add-camera-name-${camera.id}`}
                    label="Camera Name"
                    labelClassName="text-sm text-white-100"
                    required
                    {...register('name', {
                        required: 'Camera name is required',
                        minLength: {
                            value: 3,
                            message: 'Name must be at least 3 characters',
                        },
                        maxLength: {
                            value: 50,
                            message: 'Name must be less than 50 characters',
                        },
                    })}
                    placeholder="e.g Front Door Camera"
                    error={errors.name?.message}
                />
            </div>

            <div>
                <TextField
                    id={`add-camera-stream-${camera.id}`}
                    label="Stream URL"
                    labelClassName="text-sm text-white-100"
                    required
                    {...register('streamUrl', {
                        required: 'Stream URL is required',
                        pattern: {
                            value: /^[a-zA-Z0-9][\w.-]+(:\d+)?(\/.*)?$/,
                            message:
                                'Please enter a valid stream address (e.g., example.com/stream or 192.168.1.100:554/stream)',
                        },
                        onChange: handleStreamUrlChange,
                    })}
                    leadingText="rtsp://"
                    placeholder="example.com/stream"
                    error={errors.streamUrl?.message}
                    onPaste={handleStreamUrlPaste}
                />
                {!errors.streamUrl?.message && (
                    <p className="text-xs text-grey-200 mt-1">
                        Enter the streaming address for your camera
                    </p>
                )}
            </div>

            <div className="grid grid-cols-2 gap-3">
                <TextField
                    label="Username"
                    labelClassName="text-sm text-white-100"
                    {...register('username')}
                    placeholder="e.g Admin"
                />
                <TextField
                    label="Password"
                    labelClassName="text-sm text-white-100"
                    {...register('password')}
                    type="password"
                    placeholder="********"
                />
            </div>

            {(connectionStatus?.status === ConnectionStatus.TESTING ||
                connectionStatus?.status === ConnectionStatus.SUCCESS ||
                connectionStatus?.status === ConnectionStatus.ERROR) &&
                (connectionStatus.lastChecked || connectionStatus.message) && (
                    <div
                        key={`status-${camera.id}`}
                        className={`flex items-center gap-3 p-3 bg-grey-600 rounded-[8px] ${
                            shouldAnimate
                                ? 'animate-in slide-in-from-top-2 fade-in duration-300'
                                : ''
                        }`}
                    >
                        {(connectionStatus.status ===
                            ConnectionStatus.SUCCESS ||
                            (connectionStatus.status ===
                                ConnectionStatus.TESTING &&
                                connectionStatus.lastChecked)) && (
                            <>
                                <CheckCircleIcon className="w-4 h-4 text-success-100 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-white-100">
                                        Camera connection verified successfully
                                    </p>
                                    <p className="text-xs text-grey-300 mt-1">
                                        Last checked:{' '}
                                        {formatRelativeTimeI18n(
                                            connectionStatus.lastChecked!,
                                        )}
                                    </p>
                                </div>
                                {connectionStatus.status ===
                                ConnectionStatus.TESTING ? (
                                    <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                        <Spinner size={16} variant="light" />
                                    </div>
                                ) : (
                                    <button
                                        onClick={onRetryConnection}
                                        className="flex-shrink-0 text-white-100 hover:text-grey-200 transition-colors cursor-pointer"
                                        aria-label="Retry connection"
                                    >
                                        <ArrowCounterClockwiseIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </>
                        )}

                        {(connectionStatus.status === ConnectionStatus.ERROR ||
                            (connectionStatus.status ===
                                ConnectionStatus.TESTING &&
                                connectionStatus.message)) && (
                            <>
                                <WarningIcon className="w-4 h-4 text-[#F7D070] flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-white-100">
                                        Camera connection failed:{' '}
                                        {connectionStatus.message}
                                    </p>
                                    <p className="text-xs text-grey-300 mt-1">
                                        Last checked: Just now
                                    </p>
                                </div>
                                {connectionStatus.status ===
                                ConnectionStatus.TESTING ? (
                                    <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                        <Spinner size={16} variant="light" />
                                    </div>
                                ) : (
                                    <button
                                        onClick={onRetryConnection}
                                        className="flex-shrink-0 text-white-100 hover:text-grey-200 transition-colors cursor-pointer"
                                        aria-label="Retry connection"
                                    >
                                        <ArrowCounterClockwiseIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}

            <AnalysisSettings
                faceRecognition={watch('faceRecognition')}
                onFaceRecognitionChange={(checked) =>
                    setValue('faceRecognition', checked, {
                        shouldValidate: true,
                    })
                }
                licensePlateRecognition={watch('licensePlateRecognition')}
                onLicensePlateRecognitionChange={(checked) =>
                    setValue('licensePlateRecognition', checked, {
                        shouldValidate: true,
                    })
                }
                opticalCharacterRecognition={watch(
                    'opticalCharacterRecognition',
                )}
                onOpticalCharacterRecognitionChange={(checked) =>
                    setValue('opticalCharacterRecognition', checked, {
                        shouldValidate: true,
                    })
                }
                analysisFramesPerSecond={watch('analysisFramesPerSecond')}
                onAnalysisFramesPerSecondChange={(value) =>
                    setValue('analysisFramesPerSecond', value, {
                        shouldValidate: true,
                    })
                }
            />
        </form>
    );
}
